import React, { useEffect, useState, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import useForm from "../../../hooks/useForm";
import { FORM_OPTIONS } from "../table.config";
import { fetchClients } from "../../../redux/reducers/clients/clientsSlice";
import { useAuth } from "../../../hooks/useAuth";
import JobsTable from "../Create/JobsTable";
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpIcon,
} from "@heroicons/react/24/outline";
import SelectedJobsTable from "../Create/SelectedJobsTable";
import { renderAmount } from "../../../utils";
import { toast } from "react-toastify";
import Tabs from "../../../shared/Tabs";
import Spinner from "../../../shared/Spinner";
import { SidebarContainer } from "../../../components";
import { Button, EditableText } from "../../../shared";
import {
  useGetInvoiceQuery,
  useUpdateInvoiceMutation,
} from "../../../redux/services/invoices/invoicesApi";
import { useGetJobsQuery } from "../../../redux/services/jobs/jobsApi";
import { useFetchAll } from "../../../hooks/useFetchAll";

const InvoiceEdit = () => {
  const dispatch = useDispatch();
  const { user } = useAuth();
  const params = useParams();
  const navigate = useNavigate();
  const table1Ref = useRef();
  const table2Ref = useRef();

  const { form, setFormInit } = useForm(FORM_OPTIONS);

  const clientsStatus = useSelector((state) => state.clients.status);

  // Update invoice function
  const [updateInvoice, { isLoading: invoiceUpdating }] =
    useUpdateInvoiceMutation();

  // Invoice Query
  const { data: invoiceData, isLoading } = useGetInvoiceQuery({
    id: params.id,
    drivers: true,
  });
  const invoice = invoiceData || {};

  // Compute default filters based on invoice data
  const defaultFilters = useMemo(() => {
    if (!invoice?.client?._id) return [];

    return [
      { id: "client", value: invoice.client._id },
      { id: "invoiceId", value: false },
    ];
  }, [invoice?.client?._id]);

  // Jobs Query with useFetchAll hook for search and pagination functionality
  const {
    pagination: { pageIndex, pageSize, setPagination },
    search: { handleGlobalSearch, search },
    queryResult: { data: jobsQueryResult, isLoading: jobsLoading },
  } = useFetchAll({
    queryHook: useGetJobsQuery,
    defaultPageSize: 20,
    defaultFilters,
    options: {
      skip: !invoice?.client?._id, // Only skip if no client ID
    },
  });

  // Pinned client + invoice scope comes from `defaultFilters` → `baseFilters` inside useFetchAll.
  // Do not call setFilters(defaultFilters) in an effect: it re-applied filters on every render
  // (unstable setFilters identity) and reset pagination to page 1 after every navigation.

  // Extract jobs data from query result - handle different response formats
  const jobs = useMemo(() => {
    if (!jobsQueryResult) return [];

    // Handle different response structures:
    // 1. { jobs: [...], data: { page: ... } } - paginated response
    // 2. { data: { jobs: [...] } } - nested response
    // 3. [...] - direct array
    let jobsArray = [];

    if (Array.isArray(jobsQueryResult)) {
      jobsArray = jobsQueryResult;
    } else if (Array.isArray(jobsQueryResult?.jobs)) {
      jobsArray = jobsQueryResult.jobs;
    } else if (Array.isArray(jobsQueryResult?.data?.jobs)) {
      jobsArray = jobsQueryResult.data.jobs;
    } else if (Array.isArray(jobsQueryResult?.data)) {
      jobsArray = jobsQueryResult.data;
    }

    // Ensure we return an array
    return Array.isArray(jobsArray) ? jobsArray : [];
  }, [jobsQueryResult]);

  // Extract pagination data from query result
  const paginationData = useMemo(() => {
    if (!jobsQueryResult) {
      return {
        pageIndex,
        pageSize,
        pageCount: 0,
        totalPages: 0,
        totalRows: 0,
        hasNext: false,
        hasPrev: false,
      };
    }

    // Handle different response structures
    const data = jobsQueryResult.data || jobsQueryResult;
    const jobsArray = Array.isArray(jobsQueryResult)
      ? jobsQueryResult
      : Array.isArray(jobsQueryResult?.jobs)
      ? jobsQueryResult.jobs
      : Array.isArray(jobsQueryResult?.data?.jobs)
      ? jobsQueryResult.data.jobs
      : Array.isArray(jobsQueryResult?.data)
      ? jobsQueryResult.data
      : [];

    const result = {
      // Use hook state so the table stays in sync with the request (server may omit `page`).
      pageIndex,
      pageSize: data?.limit || pageSize || 20,
      pageCount: data?.totalPages || 0,
      totalPages: data?.totalPages || 0,
      totalRows: data?.count || data?.total || jobsArray.length || 0,
      hasNext: data?.hasNext || false,
      hasPrev: data?.hasPrev || false,
    };

    return result;
  }, [pageIndex, pageSize, jobsQueryResult]);

  const [selectedJobs, setSelectedJobs] = useState([]);
  const [selectedInvoiceItems, setSelectedInvoiceItems] = useState([]);
  const [invoiceItems, setInvoiceItems] = useState([]);
  const [availableJobs, setAvailableJobs] = useState([]);
  const [shouldFetchClients, setShouldFetchClients] = useState(false);

  // Update available jobs from query result, filtering out jobs already in invoice
  useEffect(() => {
    if (!jobs || jobs.length === 0) {
      // If no jobs from query, clear availableJobs (but don't clear if still loading)
      if (!jobsLoading) {
        setAvailableJobs([]);
      }
      return;
    }

    // Filter out jobs that are already in invoiceItems to avoid duplicates
    // Handle both ObjectId and string comparisons
    const invoiceJobIds = new Set(
      invoiceItems
        .map((item) => {
          const id = item._id || item;
          return typeof id === "string" ? id : id?.toString();
        })
        .filter(Boolean)
    );

    const filteredJobs = jobs.filter((job) => {
      if (!job || !job._id) return false;
      const jobId = typeof job._id === "string" ? job._id : job._id.toString();
      return !invoiceJobIds.has(jobId);
    });

    setAvailableJobs(filteredJobs);

    // Reset selected jobs when doing a search
    if (search.useSearch) {
      setSelectedJobs([]);
    }
  }, [jobs, invoiceItems, search.useSearch, jobsLoading]);

  useEffect(() => {
    if (invoice._id) {
      setFormInit(invoice);
      setInvoiceItems(invoice.jobs);
    }
  }, [invoice._id]);

  useEffect(() => {
    if (shouldFetchClients && clientsStatus === "idle") {
      dispatch(fetchClients(user.parentCompany));
      setShouldFetchClients(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldFetchClients, clientsStatus]);

  function addToInvoice() {
    setInvoiceItems([...invoiceItems, ...selectedJobs]);
    setSelectedJobs([]);
    if (table1Ref.current) {
      table1Ref.current.resetSelectedRows(false);
    }
  }

  function removeFromInvoice() {
    const newInvoiceItems = invoiceItems.filter(
      (x) => !selectedInvoiceItems.includes(x)
    );
    setInvoiceItems(newInvoiceItems);
    setSelectedInvoiceItems([]);
    if (table2Ref.current) {
      table2Ref.current.resetSelectedRows(false);
    }
  }

  async function handleSubmit() {
    const data = {
      jobs: invoiceItems.map(({ _id }) => ({ _id })),
      invoiceId: form._id,
    };

    const res = await updateInvoice({
      data,
      id: params.id,
    }).unwrap();

    toast(res.message, {
      type: res.status,
      toastId: "updated-invoice",
    });

    if (res.status === "success") {
      navigate(`/invoices/${res.data._id}`);
    }
  }

  const renderInvoiceTotal = () => {
    let totalInvoiceCharges = invoiceItems
      .map((item) => {
        return item.totalCharges;
      })
      .reduce((partialSum, a) => Number(partialSum) + Number(a), 0);

    return totalInvoiceCharges;
  };

  const totalCharges = (
    <div className="flex items-center">
      Total Charges:{" "}
      <span className="font-bold pl-2 text-xl">
        {renderAmount(renderInvoiceTotal())}
      </span>
    </div>
  );

  const tabs = [
    {
      id: "Edit",
      content: (
        <div className="h-auto">
          {form.client && (
            <div className="flex flex-col xl:flex-row justify-evenly justify-items-stretch space-x-0 xl:space-x-2">
              <div className="flex-grow w-full">
                <JobsTable
                  data={availableJobs}
                  setSelectedJobs={setSelectedJobs}
                  client={form.client}
                  ref={table1Ref}
                  pagination={paginationData}
                  pageIndex={paginationData.pageIndex}
                  pageSize={paginationData.pageSize}
                  onPaginationChange={setPagination}
                  fullTextSearch={handleGlobalSearch}
                  loading={jobsLoading}
                />
              </div>
              <div className="flex-grow-0 self-center z-50">
                <div className="hidden xl:block">
                  <IconButton icon={ChevronRightIcon} onClick={addToInvoice} />
                  <IconButton
                    icon={ChevronLeftIcon}
                    onClick={removeFromInvoice}
                  />
                </div>
                <div className="flex xl:hidden">
                  <IconButton
                    icon={ChevronUpIcon}
                    onClick={removeFromInvoice}
                  />
                  <IconButton icon={ChevronDownIcon} onClick={addToInvoice} />
                </div>
              </div>
              <div className="flex-grow w-full">
                <SelectedJobsTable
                  setSelectedInvoiceItems={setSelectedInvoiceItems}
                  selectedJobs={invoiceItems}
                  client={form.client}
                  ref={table2Ref}
                />
              </div>
            </div>
          )}
          <div className="my-4">{totalCharges}</div>
          <div className="flex justify-end mt-4">
            <Button
              className="w-6/12 lg:w-28 mr-3"
              type="alternative"
              onClick={() => navigate(`/invoices/${params.id}`)}
            >
              Cancel
            </Button>
            <Button
              loading={invoiceUpdating}
              className="w-6/12 lg:w-28"
              disabled={invoiceUpdating}
              onClick={handleSubmit}
            >
              Save
            </Button>
          </div>
        </div>
      ),
    },
  ];

  const headerRef = useRef();
  const [headerContext, setHeaderContext] = useState();
  useEffect(() => {
    setHeaderContext(headerRef.current);
  }, [headerRef.current]);

  const renderTabHeader = (headerProps) => {
    return (
      <div ref={headerRef}>
        <header className="flex items-center justify-between px-3 py-2 text-semibold text-gray-100">
          <div>{headerProps.title}</div>
          <div className="flex items-center space-x-3">
            <div className="md:flex items-center space-x-3 text-gray-800 hidden">
              {headerProps.actions}
            </div>
          </div>
        </header>
        <div className="flex justify-between md:hidden space-x-5 px-3 py-3 bg-white border-b">
          {headerProps.actions}
        </div>
      </div>
    );
  };

  const title = (
    <EditableText
      className="ml-1"
      iconClasses="h-3 w-3"
      id="invoiceId"
      value={form.invoiceId}
      disabled
      size="sm"
      displayText={(v) => (
        <span className="text-gray-800 font-semibold text-lg">{v}</span>
      )}
    />
  );

  return (
    <SidebarContainer
      title={title}
      hideSidebar
      header={renderTabHeader}
      actions={totalCharges}
    >
      {({ parentHeight }) => {
        const offsetTop = headerContext?.offsetTop;
        const offsetHeight = headerContext?.offsetHeight;
        const wrapperStyle = { height: `${parentHeight - offsetTop}px` };
        const panelStyle = {
          height: `${parentHeight - offsetTop - offsetHeight - 38}px`,
        };
        return (
          <div style={wrapperStyle} className="h-auto w-full">
            {isLoading === "loading" ? (
              <div>
                <Spinner />{" "}
              </div>
            ) : (
              <Tabs
                className={`w-full overflow-hidden`}
                style={wrapperStyle}
                panelStyle={panelStyle}
                useSearchParams
                panelClasses={`p-2 border-t overflow-y-scroll`}
                tabListClasses="bg-white px-2 h-[38px]"
                tabclassName="mx-1"
                id="invoice-edit"
                hideTabs
                data={tabs}
              />
            )}
          </div>
        );
      }}
    </SidebarContainer>
  );
};

const IconButton = (props) => {
  const Icon = props.icon;
  return (
    <div className="flex flex-col items-center my-4 mx-1">
      <div
        onClick={props.onClick}
        className="border border-gray-300 ease-in-out duration-300 hover:border-gray-300 rounded-full p-2 hover:bg-gray-200 cursor-pointer"
      >
        <Icon className="w-6 h-6" />
      </div>
      {props.label && <p className="font-semibold">{props.label}</p>}
    </div>
  );
};

export default InvoiceEdit;
